import React from 'react';
import { ChevronRight, ClipboardList } from 'lucide-react';
import { AMPEL_LABEL } from '../planungsphase/planungAmpel';
import { AMPEL_TEXT, listenHinweisZeilen } from '../planungsphase/planungKachel';
import { AmpelPunkt } from '../planungsphase/PlanungAmpelAnzeige';
import { farbenKurz, type PlanungZeile, zaehleFarben } from './planungHandlungsbedarf';
import { LinkButton, SkeletonRows } from './dashboardUi';
import type { DashboardProject } from './dashboardModel';

/** Was der Aufgaben-Baustein ueber die Planungsphasen weiss. */
export interface PlanungHandlungsbedarfStand {
    zeilen: ReadonlyArray<PlanungZeile>;
    laden: boolean;
    fehler: string | null;
}

interface Props extends PlanungHandlungsbedarfStand {
    limit?: number;
    /** Oeffnet das Projekt und springt ins Planungsmodul. */
    onOpen: (project: DashboardProject) => void;
}

/**
 * „Planungsphase — Handlungsbedarf": Projekte mit roter, gelber oder nicht
 * lesbarer Ampel, rot zuerst, mit Grund, darunter „Weiter: …" aus dem
 * Leitfaden (Stufe L3, `listenHinweisZeilen`) und Sprung ins Modul. Steht als
 * Abschnitt im Aufgaben-Baustein (TaskPanel) — so ist die Liste in jeder
 * Vorlage und auch auf dem Handy da, wo die Hub-Kachel fehlt.
 *
 * Kein Sammelknopf „Abgelaufene Fristen feststellen": der Schreibweg bleibt
 * im Modul (Entscheid E9), hier steht nur der Hinweis mit Sprung.
 * Nichts zu zeigen → null, der Baustein sieht aus wie bisher.
 */
export const PlanungHandlungsbedarf: React.FC<Props> = ({ zeilen, laden, fehler, limit = 5, onOpen }) => {
    const [expanded, setExpanded] = React.useState(false);
    if (!laden && !fehler && zeilen.length === 0) return null;
    const list = expanded ? zeilen : zeilen.slice(0, limit);
    const rest = zeilen.length - list.length;
    const kurz = farbenKurz(zaehleFarben(zeilen));

    return (
        <div className="border-t border-slate-100">
            <div className="px-3.5 pt-2 pb-1 flex items-center gap-2 min-w-0">
                <ClipboardList className="w-3 h-3 text-slate-400 shrink-0" aria-hidden="true" />
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 truncate">Planungsphase — Handlungsbedarf</h3>
                <span className="text-[11px] text-slate-400 tabular shrink-0">{zeilen.length}</span>
                {kurz && <span className="text-[11px] text-slate-400 truncate hidden sm:inline">· {kurz}</span>}
            </div>
            {fehler && (
                <p className="px-3.5 pb-2 text-[11px] text-red-700">Planungsstand nicht lesbar: {fehler}</p>
            )}
            {laden && zeilen.length === 0 ? (
                <SkeletonRows rows={2} />
            ) : (
                <ul className="divide-y divide-slate-100">
                    {list.map(z => {
                        const { grund, weiter, weiterVoll, fristen } = listenHinweisZeilen(z.ampel, z.weiter);
                        const rot = z.ampel.farbe === 'rot';
                        return (
                            <li key={z.id}>
                                <button
                                    type="button"
                                    onClick={() => onOpen(z.project)}
                                    title="Planungsphase öffnen"
                                    className="w-full text-left px-3.5 py-2 flex items-start gap-2.5 hover:bg-slate-50 active:bg-slate-100 transition-colors"
                                >
                                    <AmpelPunkt farbe={z.ampel.farbe} className="mt-1.5" />
                                    <span className="min-w-0 flex-1">
                                        <span className="flex items-center gap-1.5 min-w-0">
                                            {z.nummer && <span className="text-[11px] font-mono text-slate-500 shrink-0">{z.nummer}</span>}
                                            <span className="text-xs font-semibold text-slate-800 truncate">{z.name || '—'}</span>
                                            <span className={`text-[11px] shrink-0 ${AMPEL_TEXT[z.ampel.farbe]} ${rot ? 'font-semibold' : ''}`}>{AMPEL_LABEL[z.ampel.farbe]}</span>
                                        </span>
                                        <span className="block text-[11px] text-slate-500 truncate mt-0.5" title={grund}>{grund}</span>
                                        {weiter && <span className="block text-[11px] text-slate-600 truncate mt-0.5" title={weiterVoll}>{weiter}</span>}
                                        {fristen && <span className="block text-[11px] text-amber-700 truncate mt-0.5">{fristen}</span>}
                                    </span>
                                    <ChevronRight className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" aria-hidden="true" />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
            {rest > 0 && (
                <div className="px-3.5 py-2 border-t border-slate-100 text-center">
                    <LinkButton onClick={() => setExpanded(true)}>{rest} weitere mit Handlungsbedarf</LinkButton>
                </div>
            )}
            {expanded && zeilen.length > limit && (
                <div className="px-3.5 py-2 border-t border-slate-100 text-center">
                    <LinkButton onClick={() => setExpanded(false)}>Weniger anzeigen</LinkButton>
                </div>
            )}
        </div>
    );
};
